import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { qk } from '../services/queryKeys';
import {
  fetchWorkflowTemplates, fetchWorkflowTemplate, fetchWorkflowInstances, fetchEmployees,
  completeStepApi, startWorkflowApi,
  createWorkflowTemplateApi, addWorkflowStepApi, updateWorkflowStepApi, deleteWorkflowStepApi,
} from '../services/api';
import Button from '../components/Button';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import Alert from '../components/Alert';
import type { WorkflowStep } from '../types';

type StepType = 'manual' | 'document' | 'approval' | 'action';

// ─── Type chip colours ────────────────────────────────────────────────────────
const TYPE_STYLE: Record<string, React.CSSProperties> = {
  manual:       { background: '#e0f2fe', color: '#0369a1' },
  document:     { background: '#fff7ed', color: '#c2410c' },
  action:       { background: '#fef3c7', color: '#b45309' },
  approval:     { background: '#f3e8ff', color: '#7c3aed' },
  notification: { background: 'var(--paper-3,#f3f4f6)', color: 'var(--ink-3)' },
};

// ─── Inline SVG icons ─────────────────────────────────────────────────────────
const svg = (path: string, w = 13) => (
  <svg style={{ width: w, height: w, stroke: 'currentColor', fill: 'none', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }} viewBox="0 0 24 24">
    <path d={path} />
  </svg>
);
const IcoUp    = () => svg('m6 15 6-6 6 6');
const IcoDown  = () => svg('m6 9 6 6 6-6');
const IcoPen   = () => svg('M12 20h9M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4z');
const IcoTrash = () => svg('M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6');
const IcoPlay  = () => svg('M5 3l14 9-14 9z');
const IcoUp2   = () => svg('M12 21V9m0 0 4 4m-4-4-4 4M4 7V4a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v3');
const IcoPlus  = () => svg('M12 5v14M5 12h14', 14);
const IcoCheck = () => svg('M5 12l5 5L20 7', 14);
const IcoLock  = () => svg('M5 11h14v10H5zM8 11V8a4 4 0 1 1 8 0v3', 11);

// ─── Small icon button ────────────────────────────────────────────────────────
function IcoBtn({ title, onClick, children }: { title: string; onClick: () => void; children: React.ReactNode }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: hov ? 'var(--paper-3,#f3f4f6)' : 'none',
        border: '1px solid transparent',
        borderRadius: 6, cursor: 'pointer',
        color: hov ? 'var(--ink)' : 'var(--ink-3)',
        transition: 'background 0.12s, color 0.12s',
      }}
    >
      {children}
    </button>
  );
}

// ─── Toggle switch ────────────────────────────────────────────────────────────
function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: 36, height: 20, borderRadius: 10, flexShrink: 0,
        background: checked ? 'var(--accent,#4f46e5)' : 'var(--border,#d1d5db)',
        position: 'relative', cursor: 'pointer', transition: 'background 0.18s',
      }}
    >
      <div style={{
        position: 'absolute', top: 2,
        left: checked ? 18 : 2,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left 0.18s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
      }} />
    </div>
  );
}

// ─── Type chip ────────────────────────────────────────────────────────────────
function TypeChip({ type }: { type: string }) {
  const style = TYPE_STYLE[type] ?? TYPE_STYLE.manual;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px',
      borderRadius: 6, fontSize: 12, fontWeight: 500, textTransform: 'capitalize',
      ...style,
    }}>
      {type}
    </span>
  );
}

const WF_TRIGGERS = [
  { value: 'joining',      label: 'Employee joining' },
  { value: 'confirmation', label: 'Probation confirmation' },
  { value: 'transfer',     label: 'Department transfer' },
  { value: 'separation',   label: 'Employee separation' },
];

export default function Workflows() {
  const { role, profileId, employeeId: myEmployeeId, modal, openModal, closeModal, toast } = useAppStore();
  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<{ type: 'template' | 'instance'; id: string } | null>(null);
  const [viewMode, setViewMode] = useState<'builder' | 'run'>('builder');

  const [tplForm, setTplForm] = useState({ name: '', trigger: 'joining' });
  const [stepForm, setStepForm] = useState<{ title: string; type: StepType; ownerRole: string; dueOffsetDays: string; isBlocking: boolean }>
    ({ title: '', type: 'manual', ownerRole: 'hr', dueOffsetDays: '', isBlocking: false });
  const [editStepId, setEditStepId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ title: string; type: StepType; ownerRole: string; dueOffsetDays: string; isBlocking: boolean }>
    ({ title: '', type: 'manual', ownerRole: 'hr', dueOffsetDays: '', isBlocking: false });
  const [runEmpId, setRunEmpId] = useState('');

  const { data: workflowTemplates = [] } = useQuery({
    queryKey: qk.workflowTemplates(),
    queryFn: fetchWorkflowTemplates,
  });

  const selectedTemplateId = selected?.type === 'template' ? selected.id : null;
  const { data: templateDetail } = useQuery({
    queryKey: ['workflowTemplate', selectedTemplateId],
    queryFn: () => fetchWorkflowTemplate(selectedTemplateId!),
    enabled: !!selectedTemplateId,
  });

  const { data: workflowInstances = [] } = useQuery({
    queryKey: qk.workflowInstances(),
    queryFn: () => fetchWorkflowInstances(),
  });

  const { data: employees = [] } = useQuery({
    queryKey: qk.employees(),
    queryFn: fetchEmployees,
  });

  const invalidateTemplate = () => {
    queryClient.invalidateQueries({ queryKey: qk.workflowTemplates() });
    if (selectedTemplateId) queryClient.invalidateQueries({ queryKey: ['workflowTemplate', selectedTemplateId] });
  };

  const createTemplateMutation = useMutation({
    mutationFn: createWorkflowTemplateApi,
    onSuccess: (tpl) => {
      invalidateTemplate();
      setSelected({ type: 'template', id: tpl.id });
      toast('Template created');
      closeModal();
    },
    onError: () => toast('Failed to create template', 'danger'),
  });

  const addStepMutation = useMutation({
    mutationFn: addWorkflowStepApi,
    onSuccess: () => { invalidateTemplate(); toast('Step added'); closeModal(); },
    onError: () => toast('Failed to add step', 'danger'),
  });

  const updateStepMutation = useMutation({
    mutationFn: ({ stepId, data }: { stepId: string; data: Parameters<typeof updateWorkflowStepApi>[1] }) =>
      updateWorkflowStepApi(stepId, data),
    onSuccess: () => { invalidateTemplate(); toast('Step updated'); closeModal(); },
    onError: () => toast('Failed to update step', 'danger'),
  });

  const deleteStepMutation = useMutation({
    mutationFn: deleteWorkflowStepApi,
    onSuccess: () => { invalidateTemplate(); toast('Step removed'); },
    onError: () => toast('Failed to remove step', 'danger'),
  });

  const completeStepMutation = useMutation({
    mutationFn: (stepInstanceId: string) => completeStepApi(stepInstanceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.workflowInstances() });
      toast('Step marked complete');
    },
    onError: () => toast('Failed to complete step', 'danger'),
  });

  const startWorkflowMutation = useMutation({
    mutationFn: ({ templateId, employeeId }: { templateId: string; employeeId: string }) =>
      startWorkflowApi(templateId, employeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.workflowInstances() });
      toast('Workflow started');
      closeModal();
    },
    onError: () => toast('Failed to start workflow', 'danger'),
  });

  const selectedTemplate = selected?.type === 'template'
    ? (templateDetail ?? workflowTemplates.find(t => t.id === selected.id))
    : null;
  const steps: WorkflowStep[] = (templateDetail?.steps ?? []).sort((a, b) => a.seq - b.seq);

  const selectedInstance = selected?.type === 'instance' ? workflowInstances.find(i => i.id === selected.id) : null;
  const activeInstances = workflowInstances.filter(i => i.state !== 'completed');

  function moveStep(step: WorkflowStep, direction: 'up' | 'down') {
    const idx = steps.findIndex(s => s.id === step.id);
    const swapWith = steps[direction === 'up' ? idx - 1 : idx + 1];
    if (!swapWith) return;
    updateStepMutation.mutate({ stepId: step.id, data: { seq: swapWith.seq } });
    updateStepMutation.mutate({ stepId: swapWith.id, data: { seq: step.seq } });
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 20, alignItems: 'start' }}>

      {/* ── Sidebar ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Templates */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', fontWeight: 600 }}>Templates</span>
            {role === 'hr' && (
              <IcoBtn title="New template" onClick={() => { setTplForm({ name: '', trigger: 'joining' }); openModal('new_template'); }}>
                <IcoPlus />
              </IcoBtn>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {workflowTemplates.length === 0 && (
              <div style={{ padding: '8px 10px', fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>No templates yet</div>
            )}
            {workflowTemplates.map((t) => {
              const active = selected?.id === t.id && selected?.type === 'template';
              return (
                <div
                  key={t.id}
                  onClick={() => { setSelected({ type: 'template', id: t.id }); setViewMode('builder'); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '9px 10px', borderRadius: 8, cursor: 'pointer',
                    background: active ? 'var(--surface)' : 'transparent',
                    border: active ? '1px solid var(--border)' : '1px solid transparent',
                    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLDivElement).style.background = 'color-mix(in oklab,var(--ink) 5%,transparent)'; }}
                  onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 'var(--fs-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ink)' }}>{t.name}</div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 1 }}>{t.trigger} · V{t.isActive ? '1' : '—'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Running instances */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', fontWeight: 600 }}>Running instances</span>
            {role === 'hr' && (
              <IcoBtn title="Start a run" onClick={() => { setRunEmpId(''); openModal('start_run'); }}>
                <IcoPlus />
              </IcoBtn>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {activeInstances.length === 0 && (
              <div style={{ padding: '8px 10px', fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>No active instances</div>
            )}
            {activeInstances.map((inst) => {
              const subj = employees.find(e => e.id === inst.employeeId);
              const tmpl = workflowTemplates.find(t => t.id === inst.templateId);
              const active = selected?.id === inst.id && selected?.type === 'instance';
              return (
                <div
                  key={inst.id}
                  onClick={() => { setSelected({ type: 'instance', id: inst.id }); setViewMode('run'); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '9px 10px', borderRadius: 8, cursor: 'pointer',
                    background: active ? 'var(--surface)' : 'transparent',
                    border: active ? '1px solid var(--border)' : '1px solid transparent',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLDivElement).style.background = 'color-mix(in oklab,var(--ink) 5%,transparent)'; }}
                  onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                >
                  {subj && (
                    <div style={{
                      width: 26, height: 26, borderRadius: '50%',
                      background: subj.avatarColor || 'var(--accent)',
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 9, fontWeight: 700, flexShrink: 0, letterSpacing: '-0.5px',
                    }}>
                      {subj.firstName[0]}{subj.lastName?.[0] ?? ''}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 'var(--fs-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ink)' }}>
                      {subj ? `${subj.firstName} ${subj.lastName ?? ''}` : '—'}
                    </div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', textTransform: 'capitalize', marginTop: 1 }}>
                      {tmpl?.trigger ?? '—'}
                    </div>
                  </div>
                  {inst.blockingRemaining > 0 && (
                    <span style={{
                      width: 20, height: 20, borderRadius: '50%',
                      background: '#fee2e2', color: 'var(--danger,#dc2626)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700, flexShrink: 0,
                    }}>
                      {inst.blockingRemaining}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Canvas ── */}
      <div>
        {!selected && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--ink-3)', textAlign: 'center' }}>
            <div>
              <Icon name="flow" size={40} color="var(--border)" />
              <p style={{ marginTop: 12, fontSize: 'var(--fs-md)' }}>Select a template or instance</p>
            </div>
          </div>
        )}

        {/* ── Builder ── */}
        {selectedTemplate && viewMode === 'builder' && (
          <div className="t-card">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <div>
                <h3 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, margin: 0, letterSpacing: '-0.01em' }}>{selectedTemplate.name}</h3>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 3 }}>
                  Trigger: {selectedTemplate.trigger} · V1
                </div>
              </div>
              {role === 'hr' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="ghost" size="sm" onClick={() => { setRunEmpId(''); openModal('start_run'); }}>
                    <IcoPlay /> Start a run
                  </Button>
                  <Button variant="accent" size="sm" onClick={() => toast('Version published', 'success')}>
                    <IcoUp2 /> Publish version
                  </Button>
                </div>
              )}
            </div>

            <div style={{ padding: '16px 20px', background: 'var(--paper-2,var(--bg))', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {steps.map((step, idx) => (
                <div
                  key={step.id}
                  style={{
                    padding: '14px 16px',
                    borderRadius: 10,
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%',
                      background: 'var(--paper-3,#f3f4f6)', border: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--ink-2)', flexShrink: 0,
                    }}>
                      {idx + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ marginBottom: 5 }}>
                        <TypeChip type={step.stepType} />
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 'var(--fs-lg)', letterSpacing: '-0.01em', color: 'var(--ink)', lineHeight: 1.25 }}>
                        {step.title}
                      </div>
                      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', marginTop: 3 }}>
                        Owner: {step.ownerRole ? step.ownerRole.toUpperCase() : 'HR'}
                        {step.dueOffsetDays != null ? ` · Due ${step.dueOffsetDays > 0 ? '+' : ''}${step.dueOffsetDays}d from trigger` : ''}
                      </div>
                    </div>
                    {role === 'hr' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        <IcoBtn title="Move up" onClick={() => moveStep(step, 'up')}><IcoUp /></IcoBtn>
                        <IcoBtn title="Move down" onClick={() => moveStep(step, 'down')}><IcoDown /></IcoBtn>
                        <IcoBtn title="Edit" onClick={() => {
                          setEditStepId(step.id);
                          setEditForm({ title: step.title, type: step.stepType as StepType, ownerRole: step.ownerRole ?? 'hr', dueOffsetDays: step.dueOffsetDays != null ? String(step.dueOffsetDays) : '', isBlocking: step.isBlocking });
                          openModal('edit_wf_step');
                        }}><IcoPen /></IcoBtn>
                        <IcoBtn title="Remove" onClick={() => deleteStepMutation.mutate(step.id)}><IcoTrash /></IcoBtn>
                        <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 4px' }} />
                        <Toggle
                          checked={step.isBlocking}
                          onChange={() => updateStepMutation.mutate({ stepId: step.id, data: { isBlocking: !step.isBlocking } })}
                        />
                        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)', fontWeight: 500 }}>Blocking</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {steps.length === 0 && (
                <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--ink-3)' }}>
                  <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--ink-2)' }}>No steps yet</div>
                  <div style={{ fontSize: 'var(--fs-sm)' }}>Add your first step below to build out this template.</div>
                </div>
              )}

              {role === 'hr' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>Add step:</span>
                  {(['manual', 'document', 'approval', 'action'] as StepType[]).map((type) => (
                    <Button key={type} variant="ghost" size="sm" onClick={() => {
                      setStepForm({ title: '', type, ownerRole: 'hr', dueOffsetDays: '', isBlocking: false });
                      openModal('add_step');
                    }}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            <div style={{ padding: '10px 20px', borderTop: '1px solid var(--border)', fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
              Builder authors config · a separate deterministic engine executes it. A blocking step can never become skippable.
            </div>
          </div>
        )}

        {/* ── Run view ── */}
        {selectedInstance && viewMode === 'run' && (() => {
          const tmpl = workflowTemplates.find(t => t.id === selectedInstance.templateId);
          const subj = employees.find(e => e.id === selectedInstance.employeeId);
          const canClose = selectedInstance.blockingRemaining === 0;
          return (
            <div className="t-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {subj && (
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%',
                      background: subj.avatarColor || 'var(--accent)', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700, flexShrink: 0,
                    }}>
                      {subj.firstName[0]}{subj.lastName?.[0] ?? ''}
                    </div>
                  )}
                  <div>
                    <h3 style={{ margin: 0, fontSize: 'var(--fs-lg)', fontWeight: 600 }}>
                      {subj ? `${subj.firstName} ${subj.lastName ?? ''}` : '—'}
                    </h3>
                    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', textTransform: 'capitalize' }}>
                      {tmpl?.name ?? '—'} · started {selectedInstance.startedAt.slice(0, 10)}
                    </span>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setViewMode('builder'); setSelected({ type: 'template', id: selectedInstance.templateId }); }}>
                  ← Back to builder
                </Button>
              </div>

              <div style={{ padding: '16px 20px' }}>
                {!canClose && selectedInstance.blockingRemaining > 0 && (
                  <Alert variant="danger" style={{ marginBottom: 16 }}>
                    <strong>This workflow cannot close.</strong>{' '}
                    {selectedInstance.blockingRemaining} blocking step{selectedInstance.blockingRemaining > 1 ? 's' : ''} remain.
                  </Alert>
                )}
                {canClose && (
                  <Alert variant="success" style={{ marginBottom: 16 }}>
                    <strong>All blocking steps complete.</strong> This workflow is ready to close.
                  </Alert>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {selectedInstance.stepInstances.map((si) => {
                    const done = !!si.completedAt;
                    const canComplete = !done && (si.ownerEmployeeId === myEmployeeId || role === 'hr' || role === 'mss');
                    return (
                      <div key={si.id} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 14px', borderRadius: 8,
                        border: '1px solid var(--border)', background: 'var(--surface)',
                        opacity: done ? 0.6 : 1,
                      }}>
                        {done ? (
                          <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#d1fae5', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                            <IcoCheck />
                          </div>
                        ) : (
                          <div style={{ width: 24, height: 24, borderRadius: '50%', border: '2px dashed var(--border)', flexShrink: 0 }} />
                        )}

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: 'var(--fs-sm)' }}>{si.title}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 4 }}>
                            <TypeChip type={si.stepType} />
                            {si.isBlocking && !done && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, height: 22, padding: '0 8px', borderRadius: 6, background: '#fef3c7', color: '#b45309', fontSize: 11, fontWeight: 500 }}>
                                <IcoLock /> blocking
                              </span>
                            )}
                          </div>
                        </div>

                        {canComplete && (
                          <Button
                            variant="accent"
                            size="sm"
                            disabled={completeStepMutation.isPending}
                            onClick={() => completeStepMutation.mutate(si.id)}
                          >
                            Mark done
                          </Button>
                        )}
                        {done && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 8px', borderRadius: 6, background: '#d1fae5', color: '#059669', fontSize: 11, fontWeight: 600 }}>
                            done
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── New template modal ── */}
      {modal.type === 'new_template' && (
        <Modal title="New workflow template" onClose={closeModal} footer={
          <>
            <Button variant="ghost" onClick={closeModal}>Cancel</Button>
            <Button
              variant="accent"
              disabled={createTemplateMutation.isPending}
              onClick={() => {
                if (!tplForm.name.trim()) { toast('Enter a template name', 'warn'); return; }
                createTemplateMutation.mutate({ name: tplForm.name.trim(), trigger: tplForm.trigger });
              }}
            >
              Create template
            </Button>
          </>
        }>
          <div className="form-row">
            <label>Template name</label>
            <input className="t-input" placeholder="e.g. Probation Review" value={tplForm.name} onChange={e => setTplForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="form-row">
            <label>Trigger event</label>
            <select className="t-input" value={tplForm.trigger} onChange={e => setTplForm(f => ({ ...f, trigger: e.target.value }))}>
              {WF_TRIGGERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <Alert variant="info">Steps can be added after creating the template.</Alert>
        </Modal>
      )}

      {/* ── Add step modal ── */}
      {modal.type === 'add_step' && selectedTemplate && (
        <Modal title="Add step" onClose={closeModal} footer={
          <>
            <Button variant="ghost" onClick={closeModal}>Cancel</Button>
            <Button
              variant="accent"
              disabled={addStepMutation.isPending}
              onClick={() => {
                if (!stepForm.title.trim()) { toast('Enter a step title', 'warn'); return; }
                addStepMutation.mutate({
                  templateId: selectedTemplate.id,
                  seq: steps.length > 0 ? Math.max(...steps.map(s => s.seq)) + 10 : 10,
                  stepType: stepForm.type,
                  title: stepForm.title.trim(),
                  ownerRole: stepForm.ownerRole,
                  dueOffsetDays: stepForm.dueOffsetDays ? parseInt(stepForm.dueOffsetDays, 10) : undefined,
                  isBlocking: stepForm.isBlocking,
                });
              }}
            >
              Add step
            </Button>
          </>
        }>
          <div style={{ marginBottom: 14 }}>
            <TypeChip type={stepForm.type} />
          </div>
          <div className="form-row">
            <label>Step title</label>
            <input className="t-input" placeholder="e.g. IT equipment handover" value={stepForm.title} autoFocus onChange={e => setStepForm(f => ({ ...f, title: e.target.value }))} />
          </div>
          <div className="form-row">
            <label>Owner role</label>
            <select className="t-input" value={stepForm.ownerRole} onChange={e => setStepForm(f => ({ ...f, ownerRole: e.target.value }))}>
              <option value="hr">HR Admin</option>
              <option value="mss">Manager</option>
              <option value="ess">Employee</option>
            </select>
          </div>
          <div className="form-row">
            <label>Due offset (days from trigger)</label>
            <input className="t-input" type="number" placeholder="e.g. 1 or -2" value={stepForm.dueOffsetDays} onChange={e => setStepForm(f => ({ ...f, dueOffsetDays: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
            <Toggle checked={stepForm.isBlocking} onChange={() => setStepForm(f => ({ ...f, isBlocking: !f.isBlocking }))} />
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>Blocking — workflow cannot close until this step is done</span>
          </div>
        </Modal>
      )}

      {/* ── Edit step modal ── */}
      {modal.type === 'edit_wf_step' && selectedTemplate && editStepId && (
        <Modal title="Edit workflow step" onClose={closeModal} footer={
          <Button
            variant="accent"
            disabled={updateStepMutation.isPending}
            onClick={() => {
              if (!editForm.title.trim()) { toast('Enter a step title', 'warn'); return; }
              updateStepMutation.mutate({
                stepId: editStepId,
                data: {
                  stepType: editForm.type,
                  title: editForm.title.trim(),
                  ownerRole: editForm.ownerRole,
                  dueOffsetDays: editForm.dueOffsetDays ? parseInt(editForm.dueOffsetDays, 10) : null,
                  isBlocking: editForm.isBlocking,
                },
              });
            }}
          >
            Done
          </Button>
        }>
          <div className="form-row">
            <label>Step title</label>
            <input className="t-input" value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-row">
              <label>Step type</label>
              <select className="t-input" value={editForm.type} onChange={e => setEditForm(f => ({ ...f, type: e.target.value as StepType }))}>
                <option value="manual">Manual</option>
                <option value="document">Document</option>
                <option value="approval">Approval</option>
                <option value="action">Action</option>
              </select>
            </div>
            <div className="form-row">
              <label>Owner</label>
              <select className="t-input" value={editForm.ownerRole} onChange={e => setEditForm(f => ({ ...f, ownerRole: e.target.value }))}>
                <option value="hr">HR Admin</option>
                <option value="mss">Manager</option>
                <option value="ess">Employee</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <label>Due offset (days from trigger)</label>
            <input className="t-input" type="number" value={editForm.dueOffsetDays} onChange={e => setEditForm(f => ({ ...f, dueOffsetDays: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
            <Toggle checked={editForm.isBlocking} onChange={() => setEditForm(f => ({ ...f, isBlocking: !f.isBlocking }))} />
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>Blocking — workflow cannot close until this step is done</span>
          </div>
        </Modal>
      )}

      {/* ── Start run modal ── */}
      {modal.type === 'start_run' && (
        <Modal title="Start a workflow run" onClose={closeModal} footer={
          <>
            <Button variant="ghost" onClick={closeModal}>Cancel</Button>
            <Button
              variant="accent"
              disabled={startWorkflowMutation.isPending}
              onClick={() => {
                const tplId = selectedTemplate?.id ?? workflowTemplates[0]?.id;
                if (!tplId) { toast('Select a template first', 'warn'); return; }
                if (!runEmpId) { toast('Select a subject employee', 'warn'); return; }
                startWorkflowMutation.mutate({ templateId: tplId, employeeId: runEmpId });
              }}
            >
              Start run
            </Button>
          </>
        }>
          <div className="form-row">
            <label>Template</label>
            <div style={{ padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 'var(--fs-sm)', background: 'var(--paper-2,var(--bg))', color: 'var(--ink-2)' }}>
              {selectedTemplate?.name ?? '— select a template from the sidebar first —'}
            </div>
          </div>
          <div className="form-row">
            <label>Subject employee</label>
            <select className="t-input" value={runEmpId} onChange={e => setRunEmpId(e.target.value)}>
              <option value="">Select employee…</option>
              {employees.filter(e => e.status === 'active').map(e => (
                <option key={e.id} value={e.id}>{e.firstName} {e.lastName} · {e.employeeCode}</option>
              ))}
            </select>
          </div>
        </Modal>
      )}
    </div>
  );
}
